import { ChevronRight, Lock } from "lucide-react";

import { AccessCardField } from "@/modules/dashboard/settings/components/access/AccessCardField";
import { AccessEffectBadge } from "@/modules/dashboard/settings/components/access/AccessEffectBadge";
import { AccessScopeBadge } from "@/modules/dashboard/settings/components/access/AccessScopeBadge";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { PolicyRow } from "../hooks/usePoliciesCatalog";
import { PolicyStatementsCell } from "./PolicyStatementsCell";

export interface PoliciesCardsProps {
  rows: readonly PolicyRow[];
  loading: boolean;
  /** The roles catalog could not be read, so no policy's usage is known. */
  usageUnknown: boolean;
  onOpen: (row: PolicyRow) => void;
}

/** The same rows as `PoliciesTable`, below `lg`. Same data, same opening behaviour, one card per policy. */
export function PoliciesCards({ rows, loading, usageUnknown, onOpen }: PoliciesCardsProps) {
  const { t } = useTranslations("settings");

  if (loading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <ColocaSkeleton className="h-36 w-full" />
        <ColocaSkeleton className="h-36 w-full" />
        <ColocaSkeleton className="h-36 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {rows.map((row) => {
        const { policy } = row;
        const isDeny = policy.effect === "deny";
        return (
          <button
            key={policy.id}
            type="button"
            onClick={() => onOpen(row)}
            className={cn(
              "relative flex flex-col gap-2.5 rounded-md border bg-card p-3.5 text-left transition-colors",
              isDeny
                ? "border-destructive/30 hover:border-destructive/50"
                : "border-border hover:border-border/70",
              !row.editable && "opacity-70",
            )}
          >
            <span
              className={cn(
                "absolute bottom-0 left-0 top-0 w-[3px] rounded-l-md",
                isDeny ? "bg-destructive" : "bg-primary",
              )}
              aria-hidden
            />
            <div className="flex items-center gap-1.5 pl-1.5">
              <span className="truncate text-[13.5px] font-semibold text-foreground">{policy.name}</span>
              {policy.is_protected && (
                <Lock
                  size={11}
                  className="shrink-0 text-muted-foreground/70"
                  aria-label={t("access_policies_protected_hint")}
                />
              )}
              <ChevronRight size={14} className="ml-auto shrink-0 text-muted-foreground/60" />
            </div>

            {policy.description && (
              <div className="pl-1.5 text-[11.5px] leading-snug text-muted-foreground">
                {policy.description}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 pl-1.5">
              <AccessCardField label={t("access_policies_col_effect")}>
                <AccessEffectBadge effect={policy.effect} />
              </AccessCardField>
              <AccessCardField label={t("access_page_col_scope")}>
                <AccessScopeBadge organizationId={policy.organization_id} />
              </AccessCardField>
            </div>

            <div className="pl-1.5">
              <AccessCardField label={t("access_policies_col_permissions")}>
                <PolicyStatementsCell
                  statements={policy.statements}
                  effect={policy.effect}
                  resourceCount={row.resourceCount}
                />
              </AccessCardField>
            </div>

            <div className="border-t border-dashed border-border pl-1.5 pt-2">
              {usageUnknown ? (
                <span
                  className="font-mono text-[10.5px] text-muted-foreground/70"
                  title={t("access_policies_usage_unknown_title")}
                >
                  {t("access_policies_col_used_by")}: ?
                </span>
              ) : row.usedBy === 0 ? (
                <ColocaBadge variant="warning">{t("access_policies_unused_badge")}</ColocaBadge>
              ) : (
                <span className="font-mono text-[10.5px] text-muted-foreground">
                  {row.usedBy === 1
                    ? t("access_policy_used_by_singular")
                    : t("access_policy_used_by_plural", { count: row.usedBy })}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}
